// Spec 26 time-attendance — leave window → working days → hours (SEM-7211).
//
// The draw used to be whatever the client posted, with the calendar never
// consulted; it is derived here instead, from the window and the tenant's
// holiday calendar (entity_holiday, SEM-7207). 4857/56 is the rule: "yıllık
// ücretli izin günlerinin hesabında izin süresine rastlayan ulusal bayram,
// hafta tatili ve genel tatil günleri izin süresinden sayılmaz."
//
// THE LEAVE-DAY RULE (LT-11). Everything below implements this one sentence:
//
//   A day is a leave day when its weekday is a working weekday of the tenant's
//   working week IN FORCE ON THAT DATE (`working-week.ts`) and it is not a
//   full-day public holiday.
//
// Consequences that callers rely on, each pinned by leave-duration.test.ts:
//   * A day that is not a leave day draws nothing wherever it sits in the range:
//     inside it, or on either edge. A range that starts on a Saturday and one
//     that starts on the following Monday draw the same hours, so the draw of a
//     range equals the sum of the draws of any partition of it, and equals the
//     draw of the range trimmed to its first and last leave day.
//   * Where a range STARTS is therefore not its start date but its first leave
//     day (`firstLeaveDay`): that is the day tenure and service-year rules are
//     judged on, so a request typed from the Saturday before the anniversary
//     Monday is not a different request from one typed from that Monday.
//   * Every leave type counts leave days this way today. For annual leave that is
//     4857 m.56 ("hafta tatili ve genel tatil günleri izin süresinden sayılmaz");
//     applying the same definition to the other types is a product choice, not
//     something m.56 says, and whether a type such as paid excuse leave counts
//     calendar days instead is owner-pending (dayanak teyit bekliyor).
//   * Payroll's unpaid-leave deduction is NOT this rule: a non-windowed unpaid
//     request deducts every CALENDAR day of its range, weekends included (owner-
//     pending decision #6). Only its handling of windowed requests reads
//     `isLeaveWorkingDay`. The two are shown side by side, never reconciled here.
//
// The working WEEK is tenant configuration (LT-11); the length of a working DAY
// stays the constant below - the 4857 m.63 measure the overtime side and payroll
// divisor read too.

import type { PartialDaySpec } from "./entities.ts";
import { isStrictIsoCalendarDate, partialDayMinutes } from "./pto.ts";
import { isWorkingWeekday, type WorkingWeek } from "./working-week.ts";

/**
 * Hours one full leave day draws: 7,5 (sahip kararı K4, 4857 m.63 günlük
 * çalışma ölçüsü). Fazla mesai tarafı (overtime.ts) ve bordro saat böleni
 * aynı sabiti okur; iki taraf farklı bölenle çalışırsa bir günlük izin bir
 * tarafta 8, diğerinde 7,5 saat sayılır.
 */
export const HOURS_PER_WORKING_DAY = 7.5;

/**
 * Minutes one full leave day is worth (450). Payroll deducts partial-day and
 * hourly unpaid leave by the minute and needs the day in the same unit the
 * partial windows are written in; it is derived here, from the one hours
 * constant, so the engine's "how long is a leave day" and the leave form's cap
 * on a partial window can never be two numbers.
 */
export const MINUTES_PER_WORKING_DAY = HOURS_PER_WORKING_DAY * 60;

const MS_PER_DAY = 86_400_000;

/**
 * Longest window the derivation walks. The work is per-day, so an unbounded
 * range is an unbounded server-side loop on caller-supplied input; a year and
 * a day is well past any real leave request.
 */
export const MAX_LEAVE_WINDOW_DAYS = 366;

export interface LeaveDurationInput {
  /** YYYY-MM-DD, inclusive. */
  readonly startDate: string;
  /** YYYY-MM-DD, inclusive. */
  readonly endDate: string;
  /** Holiday days inside the window (HolidayRepo.holidayDatesInRange). */
  readonly holidayDates: readonly string[];
  readonly partialDays?: readonly PartialDaySpec[];
  /** The tenant's working week; omitted = Mon-Fri, the pre-LT-11 behaviour. */
  readonly workingWeek?: WorkingWeek;
}

export interface LeaveDurationResult {
  /** Days in the window that are neither weekend nor holiday. */
  readonly workingDays: number;
  /** Server-derived draw, normalized to the persisted decimal-hours precision. */
  readonly hours: number;
}

/**
 * The one definition of a leave day: a working weekday of the week in force that
 * day and not a full-day holiday. Leave hours, the partial-window check, the
 * timesheet's leave hours and payroll's reading of a windowed request (which rest
 * days of its range deduct nothing) all ask this, so a weekend can never be a
 * leave day on one side and a full unpaid day on the other. `date` must be a
 * valid `YYYY-MM-DD`; without `workingWeek` the week is Mon-Fri.
 */
export function isLeaveWorkingDay(
  date: string,
  holidays: ReadonlySet<string>,
  workingWeek?: WorkingWeek,
): boolean {
  return isWorkingWeekday(workingWeek, date) && !holidays.has(date);
}

function shiftIsoDay(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00.000Z`) + days * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * The first leave day of `[startDate, endDate]`, or null when the window holds
 * none (weekend / holiday only, inverted, or longer than the walk cap). This -
 * not the raw start date - is where a request begins for every rule that asks
 * "when does this leave start" (see the header).
 */
export function firstLeaveDay(input: LeaveDurationInput): string | null {
  return edgeLeaveDay(input, 1);
}

/** The last leave day of the window; the mirror of {@link firstLeaveDay}. */
export function lastLeaveDay(input: LeaveDurationInput): string | null {
  return edgeLeaveDay(input, -1);
}

function edgeLeaveDay(input: LeaveDurationInput, direction: 1 | -1): string | null {
  const dayCount = leaveWindowDayCount(input.startDate, input.endDate);
  if (dayCount === 0 || dayCount > MAX_LEAVE_WINDOW_DAYS) return null;
  const holidays = new Set(input.holidayDates);
  for (let i = 0; i < dayCount; i++) {
    const day = direction === 1 ? shiftIsoDay(input.startDate, i) : shiftIsoDay(input.endDate, -i);
    if (isLeaveWorkingDay(day, holidays, input.workingWeek)) return day;
  }
  return null;
}

/**
 * The leave day that follows `day` (exclusive), looking at most `horizonDays`
 * ahead; null when none falls inside the horizon. Two requests are ONE
 * uninterrupted leave when this is the other's first leave day: the days between
 * them are weekend or holiday, which no leave counts.
 */
export function nextLeaveDayAfter(
  day: string,
  holidays: ReadonlySet<string>,
  workingWeek: WorkingWeek | undefined,
  horizonDays: number,
): string | null {
  for (let i = 1; i <= horizonDays; i++) {
    const candidate = shiftIsoDay(day, i);
    if (isLeaveWorkingDay(candidate, holidays, workingWeek)) return candidate;
  }
  return null;
}

/** A supplied partial window must refer to a unique working day in the range. */
export function validateLeavePartialDays(input: LeaveDurationInput): readonly string[] {
  const issues: string[] = [];
  const dates = new Set<string>();
  const holidays = new Set(input.holidayDates);
  if ((input.partialDays?.length ?? 0) > MAX_LEAVE_WINDOW_DAYS) {
    return ["partial_days: too many partial-day windows"];
  }
  for (const [index, window] of (input.partialDays ?? []).entries()) {
    const field = `partial_days[${index}]`;
    if (!isStrictIsoCalendarDate(window.date) || window.date < input.startDate || window.date > input.endDate) {
      issues.push(`${field}.date: must be a calendar date within the request window`);
    } else if (!isLeaveWorkingDay(window.date, holidays, input.workingWeek)) {
      issues.push(`${field}.date: must be a working day, not a weekend or holiday`);
    }
    if (dates.has(window.date)) issues.push(`${field}.date: only one window per day is allowed`);
    dates.add(window.date);
    const minutes = partialDayMinutes(window);
    if (minutes === null || minutes > MINUTES_PER_WORKING_DAY) {
      issues.push(`${field}: start_time and end_time must be same-day HH:MM, end after start, at most ${HOURS_PER_WORKING_DAY} hours`);
    }
  }
  return issues;
}

/** Inclusive day count of a `YYYY-MM-DD` window; 0 when end precedes start. */
export function leaveWindowDayCount(startDate: string, endDate: string): number {
  const start = Date.parse(`${startDate}T00:00:00.000Z`);
  const end = Date.parse(`${endDate}T00:00:00.000Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0;
  return Math.floor((end - start) / MS_PER_DAY) + 1;
}

/**
 * Derive a leave request's working days + hours from its window. Pure; the
 * caller supplies the holiday days it read for exactly this range.
 *
 * Callers validate the dates first (`isStrictIsoCalendarDate`, end ≥ start,
 * {@link MAX_LEAVE_WINDOW_DAYS}); a window that fails any of those yields
 * zero working days here rather than a throw the route would have to unwrap.
 */
export function computeLeaveDuration(input: LeaveDurationInput): LeaveDurationResult {
  const dayCount = leaveWindowDayCount(input.startDate, input.endDate);
  if (dayCount === 0 || dayCount > MAX_LEAVE_WINDOW_DAYS || validateLeavePartialDays(input).length > 0) {
    return { workingDays: 0, hours: 0 };
  }

  const holidays = new Set(input.holidayDates);
  const start = Date.parse(`${input.startDate}T00:00:00.000Z`);

  let workingDays = 0;
  let totalMinutes = 0;
  const partials = new Map((input.partialDays ?? []).map((window) => [window.date, window]));
  for (let i = 0; i < dayCount; i++) {
    const date = new Date(start + i * MS_PER_DAY).toISOString().slice(0, 10);
    if (!isLeaveWorkingDay(date, holidays, input.workingWeek)) continue;
    workingDays += 1;
    const partial = partials.get(date);
    totalMinutes += partial === undefined ? MINUTES_PER_WORKING_DAY : partialDayMinutes(partial)!;
  }

  // entity_pto_request.hours and balances use numeric(..., 2). Round ONCE
  // after summing whole minutes so preview, storage and the later draw agree.
  return { workingDays, hours: Math.round(totalMinutes * 100 / 60) / 100 };
}
